import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Sparkles, Send, ArrowRight, ReceiptText, Upload, Banknote,
  Landmark, BarChart3, UserPlus, FileText, CheckCircle2, Bot,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { useHubData, CardError, CardEmpty } from './DateRangeSelect';

export const RESOURCE_ICONS = {
  invoice: ReceiptText, invoices: ReceiptText, document: FileText, documents: FileText,
  transaction: Landmark, bank: Landmark, expense: Banknote, contact: UserPlus,
  client: UserPlus, report: BarChart3, reconciliation: CheckCircle2, ai: Bot,
};

export const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export function RecentActivity({ wsId }) {
  const navigate = useNavigate();
  const url = wsId ? `/api/dashboard-hub/activity?workspace_id=${wsId}&limit=8` : null;
  const { data, loading, error, retry } = useHubData(url);

  return (
    <section className="rounded-2xl border border-[#E5EAF0] bg-white p-5 shadow-sm" data-testid="recent-activity-card" aria-label="Recent activity">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-[#172033]">Recent Activity</h2>
        <button type="button" onClick={() => navigate('/activity')} data-testid="activity-view-all"
          className="text-xs font-semibold text-[#118A35] hover:underline">View all</button>
      </div>
      {loading ? (
        <div className="mt-4 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />)}</div>
      ) : error ? (
        <CardError onRetry={retry} />
      ) : !data?.items?.length ? (
        <CardEmpty label="Every action gets logged here. Make your first one and watch it appear." />
      ) : (
        <ul className="mt-3 space-y-1">
          {data.items.map((item) => {
            const Icon = RESOURCE_ICONS[(item.resource || '').toLowerCase()] || Activity;
            return (
              <li key={item.id} className="flex items-start gap-3 rounded-lg px-1 py-2" data-testid="activity-item">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#20B94B]/10">
                  <Icon className="h-4 w-4 text-[#118A35]" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm capitalize text-[#172033]">{item.title}</p>
                  <p className="truncate text-xs text-[#657085]">{item.actor_name}</p>
                </div>
                <span className="shrink-0 text-xs text-[#657085]">{timeAgo(item.at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const SUGGESTED_QUESTIONS = [
  'How much did I spend on marketing last month?',
  "Which clients haven't paid me?",
  'Show me my profit trend over the last 6 months.',
  'What expenses may be tax deductible?',
];

export function AiAssistantCard() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const ask = (prompt) => navigate(`/ai-hub?seed=${encodeURIComponent(prompt)}`);

  return (
    <section className="rounded-2xl border border-[#7657F6]/25 bg-white p-5 shadow-sm" data-testid="ai-assistant-card" aria-label="AI assistant">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#7657F6]/10">
          <Sparkles className="h-4 w-4 text-[#7657F6]" aria-hidden="true" />
        </span>
        <h2 className="text-base font-bold text-[#172033]">AI Assistant</h2>
        <Badge className="bg-[#20B94B] text-[10px] text-white hover:bg-[#118A35]">New</Badge>
      </div>
      <form
        className="mt-3 flex items-center gap-2 rounded-xl border border-[#E5EAF0] px-3 py-2 focus-within:border-[#7657F6]"
        onSubmit={(e) => { e.preventDefault(); if (q.trim()) ask(q.trim()); }}
      >
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Ask anything about your business…"
          aria-label="Ask the AI assistant"
          data-testid="ai-card-input"
          className="min-w-0 flex-1 bg-transparent text-sm text-[#172033] outline-none placeholder:text-[#9AA3B2]"
        />
        <button type="submit" aria-label="Send question" data-testid="ai-card-send" className="text-[#7657F6]">
          <Send className="h-4 w-4" />
        </button>
      </form>
      <div className="mt-3 space-y-1.5">
        {SUGGESTED_QUESTIONS.map((s, i) => (
          <button
            key={s} type="button" onClick={() => ask(s)} data-testid={`ai-suggestion-${i}`}
            className="flex w-full items-center justify-between gap-2 rounded-lg border border-[#E5EAF0] px-3 py-2 text-left text-xs text-[#172033] transition-colors hover:border-[#7657F6]/50 hover:bg-[#7657F6]/5"
          >
            <span className="min-w-0 truncate">{s}</span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-[#7657F6]" />
          </button>
        ))}
      </div>
      <button type="button" onClick={() => navigate('/ai-hub')} data-testid="ai-view-conversations"
        className="mt-3 text-xs font-semibold text-[#7657F6] hover:underline">
        View all conversations
      </button>
    </section>
  );
}

const QUICK_ACTIONS = [
  { label: 'Create Invoice', icon: ReceiptText, href: '/invoices?new=1', color: '#20B94B', roles: null },
  { label: 'Upload Document', icon: Upload, href: '/documents?upload=1', color: '#7657F6', roles: null },
  { label: 'Record Expense', icon: Banknote, href: '/banking?add=expense', color: '#FF7816', roles: null },
  { label: 'Reconcile Bank', icon: Landmark, href: '/banking?tab=reconcile', color: '#2979F2', roles: ['owner', 'admin', 'accountant'] },
  { label: 'Create Report', icon: BarChart3, href: '/reports', color: '#7657F6', roles: null },
  { label: 'Add Client', icon: UserPlus, href: '/contacts?new=1', color: '#118A35', roles: null },
];

export function QuickActions({ role }) {
  const navigate = useNavigate();
  const visible = QUICK_ACTIONS.filter((a) => !a.roles || !role || a.roles.includes(role));
  return (
    <section className="rounded-2xl border border-[#E5EAF0] bg-white p-5 shadow-sm" data-testid="quick-actions-card" aria-label="Quick actions">
      <h2 className="text-base font-bold text-[#172033]">Quick Actions</h2>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {visible.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.label} type="button" onClick={() => navigate(a.href)}
              data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex min-h-[76px] flex-col items-center justify-center gap-1.5 rounded-xl border border-[#E5EAF0] p-2 text-center transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20B94B]/40"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${a.color}1A` }}>
                <Icon className="h-4 w-4" style={{ color: a.color }} aria-hidden="true" />
              </span>
              <span className="text-[11px] font-medium leading-tight text-[#172033]">{a.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function AiInsightBanner({ wsId }) {
  const navigate = useNavigate();
  const url = wsId ? `/api/dashboard-hub/insight?workspace_id=${wsId}` : null;
  const { data, loading, error } = useHubData(url);
  const insights = data?.insights?.length ? data.insights : (data?.text ? [data] : []);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (insights.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % insights.length), 8000);
    return () => clearInterval(t);
  }, [insights.length]);

  if (error) return null;
  const current = insights[Math.min(idx, Math.max(insights.length - 1, 0))];

  return (
    <section
      className="rounded-2xl border border-[#20B94B]/30 bg-[#20B94B]/5 p-5"
      data-testid="ai-insight-banner" aria-label="AI insight" aria-live="polite"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
          <Sparkles className="h-5 w-5 text-[#118A35]" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-[#172033]">AI Insight</h2>
            <Badge className="bg-[#20B94B] text-[10px] text-white hover:bg-[#118A35]">AI-generated</Badge>
            {insights.length > 1 && (
              <span className="ml-auto flex items-center gap-2 sm:ml-2">
                <button type="button" aria-label="Previous insight" data-testid="insight-prev"
                  onClick={() => setIdx((i) => (i - 1 + insights.length) % insights.length)}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-[#657085] hover:bg-white">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="flex items-center gap-1" data-testid="insight-dots">
                  {insights.map((_, i) => (
                    <span key={i} className={`h-1.5 w-1.5 rounded-full transition-colors ${i === idx ? 'bg-[#118A35]' : 'bg-[#20B94B]/25'}`} />
                  ))}
                </span>
                <button type="button" aria-label="Next insight" data-testid="insight-next"
                  onClick={() => setIdx((i) => (i + 1) % insights.length)}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-[#657085] hover:bg-white">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </span>
            )}
          </div>
          {loading ? (
            <div className="mt-1.5 h-4 w-3/4 animate-pulse rounded bg-[#20B94B]/15" />
          ) : (
            <p className="mt-1 text-sm text-[#172033]" data-testid="ai-insight-text">{current?.text}</p>
          )}
          <p className="mt-1.5 text-[11px] italic text-[#657085]">
            AI-generated insights are informational and should be reviewed before making financial, tax, or business decisions.
          </p>
        </div>
        <button
          type="button"
          data-testid="ai-insight-ask-btn"
          onClick={() => navigate(`/ai-hub?seed=${encodeURIComponent(current?.cta_prompt || 'Analyze my finances this month')}`)}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#20B94B] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#118A35]"
        >
          <Sparkles className="h-4 w-4" /> Ask AI Assistant
        </button>
      </div>
    </section>
  );
}
